import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingItemRecordList, BookingRecordList, BookingStatus, CarManagerRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import {
  addDays,
  CustomDateFormats,
  customerRoutes,
  extractDate,
  formatLicensePlate,
  getStartOfToday,
  isToday,
  isTomorrow,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { CarManagerNames } from '../../../components/Common/CarManagerNames';
import { useNewBookingPath } from '../../../hooks/useNewBookingPath';
import { useClearBookingDrafts } from '../../../hooks/useStartNewBooking';
import { formatBookingTime } from '../../../utils/fleetCalendarBookingDisplay';

const UPCOMING_STATUSES: BookingStatus[] = [BookingStatus.CONFIRMED, BookingStatus.ASSIGNED_TO_ROUTE];
const PREVIEW_WINDOW_DAYS = 30;
const PREVIEW_LIMIT = 4;
const VISIBLE_PLATES = 3;

export const UpcomingBookingsOverview = () => {
  const { currentUserGroupId } = useAuthContext();
  const clearBookingDrafts = useClearBookingDrafts();
  const { path: newBookingPath } = useNewBookingPath();

  const fromDate = noddiFormatDate(getStartOfToday(), NoddiDateFormats.API_DATE);
  const toDate = noddiFormatDate(addDays(getStartOfToday(), PREVIEW_WINDOW_DAYS), NoddiDateFormats.API_DATE);

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookings,
    input: {
      userGroupIds: [currentUserGroupId as number],
      statusIds: UPCOMING_STATUSES,
      deliveryWindowStartsAtGte: fromDate,
      deliveryWindowStartsAtLte: toDate,
      pageSize: PREVIEW_LIMIT,
      pageIndex: 0
    }
  });

  if (isPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  const bookings = data.results;
  const totalUpcoming = data.count;
  const calendarPath = customerRoutes.fleetCalendar.getPath();
  const countLabel = plural(totalUpcoming, { one: '# upcoming', other: '# upcoming' });
  const startNewBooking = () => {
    clearBookingDrafts();
  };

  return (
    <div className='flex flex-col gap-3 md:gap-4'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-xl font-bold text-primary md:text-3xl'>
          <Trans>Upcoming bookings</Trans>
        </h2>
        {totalUpcoming > 0 && (
          <p className='text-sm text-secondary'>
            <Trans>
              {countLabel} in the next {PREVIEW_WINDOW_DAYS} days
            </Trans>
          </p>
        )}
      </div>

      {bookings.length === 0 ? (
        <NoddiBasicCard to={newBookingPath} onClick={startNewBooking} className='group flex items-center gap-4 p-5'>
          <span className='flex size-12 shrink-0 items-center justify-center rounded-lg bg-card-light-background text-primary'>
            <NoddiIcon name='CalendarDays' size='medium' />
          </span>
          <span className='flex min-w-0 flex-1 flex-col gap-1'>
            <span className='font-semibold text-primary'>
              <Trans>Book a service</Trans>
            </span>
            <span className='text-sm text-muted-foreground'>
              <Trans>Choose one or more cars to get started.</Trans>
            </span>
          </span>
          <NoddiIcon
            name='ChevronRight'
            size='small'
            className='text-muted-foreground transition-transform group-hover:translate-x-1'
          />
        </NoddiBasicCard>
      ) : (
        <NoddiBasicCard to={calendarPath} className='flex flex-col p-0'>
          <ul className='flex flex-col divide-y divide-border'>
            {bookings.map((booking) => (
              <BookingRow key={booking.id} booking={booking} />
            ))}
          </ul>
          <div className='border-t border-border p-3'>
            <NoddiDummyButton variant='ghost' size='small' endIcon='ArrowRight' className='w-full justify-center'>
              <Trans>Open calendar</Trans>
            </NoddiDummyButton>
          </div>
        </NoddiBasicCard>
      )}
    </div>
  );
};

const BookingRow = ({ booking }: { booking: BookingRecordList }) => {
  const bookingDate = extractDate(booking.date);
  const dayLabel = getDayLabel(bookingDate);
  const timeWindow = formatBookingTime(booking);
  const serviceNames = getPrimaryServiceNames(booking.bookingItems);

  return (
    <li className='flex items-stretch gap-4 p-4'>
      <DateAvatar date={bookingDate} />
      <div className='flex min-w-0 flex-1 flex-col justify-center gap-1'>
        <div className='flex items-center gap-2 text-sm text-secondary'>
          <span className='font-semibold text-primary'>{dayLabel}</span>
          <span aria-hidden>•</span>
          <span>{timeWindow}</span>
        </div>
        <CarPlates items={booking.bookingItems} />
        <CarManagerNames carManagers={getCarManagers(booking.bookingItems)} />
        {serviceNames.length > 0 && <p className='truncate text-xs'>{serviceNames.join(' · ')}</p>}
      </div>
    </li>
  );
};

const getCarManagers = (items: BookingItemRecordList[]): CarManagerRecord[] => {
  const byId = new Map<number, CarManagerRecord>();
  for (const item of items) {
    for (const manager of item.car.carManagers ?? []) {
      byId.set(manager.id, manager);
    }
  }
  return Array.from(byId.values());
};

const getPrimaryServiceNames = (items: BookingItemRecordList[]): string[] => {
  const names = new Set<string>();
  for (const item of items) {
    for (const sale of item.salesItems) {
      if (sale.isPrimary) {
        names.add(sale.name);
      }
    }
  }
  return Array.from(names);
};

const DateAvatar = ({ date }: { date: Date | null | undefined }) => {
  if (!date) {
    return null;
  }
  const day = noddiFormatDate(date, CustomDateFormats.SHORT_DAY);
  const month = noddiFormatDate(date, CustomDateFormats.SHORT_MONTH);

  return (
    <div className='flex size-14 shrink-0 flex-col items-center justify-center rounded-lg bg-muted'>
      <span className='text-lg leading-none font-bold text-primary'>{day}</span>
      <span className='mt-0.5 text-xs font-semibold text-secondary uppercase'>{month}</span>
    </div>
  );
};

const CarPlates = ({ items }: { items: BookingItemRecordList[] }) => {
  const visible = items.slice(0, VISIBLE_PLATES);
  const hiddenCount = items.length - visible.length;
  const moreLabel = plural(hiddenCount, { one: '+# more', other: '+# more' });

  return (
    <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
      {visible.map((item, index) => (
        <span key={item.id} className='flex items-center gap-2'>
          <span className='truncate font-medium'>{formatLicensePlate(item.car.licensePlateNumber)}</span>
          {index < visible.length - 1 && (
            <span aria-hidden className='text-secondary'>
              •
            </span>
          )}
        </span>
      ))}
      {hiddenCount > 0 && <span className='text-xs text-secondary'>{moreLabel}</span>}
    </div>
  );
};

const getDayLabel = (date: Date | null | undefined): string => {
  if (!date) {
    return '';
  }
  if (isToday(date)) {
    return t`Today`;
  }
  if (isTomorrow(date)) {
    return t`Tomorrow`;
  }
  return noddiFormatDate(date, CustomDateFormats.FULL_WEEKDAY);
};
